import { Component, Input, OnInit, signal, WritableSignal } from '@angular/core';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { MetricsPlotsDto } from '../../../core/services/validation-run/metrics-plots.dto';
import { ValidationrunService } from '../../../core/services/validation-run/validationrun.service';
import { HttpParams } from '@angular/common/http';
import { ValidationrunDto } from '../../../core/services/validation-run/validationrun.dto';
import { WebsiteGraphicsService } from '../../../core/services/global/website-graphics.service';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { catchError, map } from 'rxjs/operators';
import { PlotDto } from '../../../core/services/global/plot.dto';
import { SafeUrl } from '@angular/platform-browser';
import { GlobalParamsService } from '../../../core/services/global/global-params.service';
import { CustomHttpError } from '../../../core/services/global/http-error.service';
import { ToastService } from '../../../core/services/toast/toast.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
@Component({
  selector: 'qa-result-files',
  templateUrl: './result-files.component.html',
  styleUrls: ['./result-files.component.scss'],
  standalone: false
})
export class ResultFilesComponent implements OnInit {
  @Input() validation: ValidationrunDto;
  faIcons = { faFileDownload: fas.faFileDownload };

  updatedMetrics$: Observable<MetricsPlotsDto[]>;

  boxplotIndx = 0;
  displayOverviewGallery: boolean;
  displayBoxplotGallery: boolean;
  displayAdditionalPlotGallery: boolean;

  activeOverviewIndex = 0;
  activeBoxplotIndex = 0;
  activeAdditionalPlotIndex = 0;

  selectedMetric: WritableSignal<MetricsPlotsDto> = signal({} as MetricsPlotsDto);
  currentOverviewFiles: WritableSignal<Observable<PlotDto[]>> = signal(of([]));

  fileError = signal(false);
  dataFetchError = signal(false);
  showInteractiveMap: boolean | null = null;

  constructor(private validationService: ValidationrunService,
    public plotService: WebsiteGraphicsService,
    public globals: GlobalParamsService,
    private toastService: ToastService,
    private http: HttpClient) {
  }

  ngOnInit(): void {
    this.updateMetricsWithPlots();
    this.checkZarrFile();
  }

  private updateMetricsWithPlots(): void {
    const params = new HttpParams().set('validationId', this.validation.id);

    this.updatedMetrics$ = this.validationService.getMetricsAndPlotsNames(params)
      .pipe(
        map((metrics) =>
          metrics.map(
            metric =>
            ({
              ...metric,
              boxplotFiles: this.getPlots(metric.boxplot_dicts.map(boxplotFile => boxplotFile.file)),
              overviewFiles: this.getPlots(metric.overview_files),
              comparisonFile: metric.comparison_boxplot.length !== 0 ? this.getPlots(metric.comparison_boxplot) : of(null)
            })
          )
        ),
        tap(data => {
          this.selectedMetric.set(data[0]);
          this.updateOverviewForMetric(data[0]);
        }),
        catchError((error: CustomHttpError) => {
          this.dataFetchError.set(true);
          this.toastService.showErrorWithHeader(error.errorMessage.header, error.errorMessage.message);
          return of([] as MetricsPlotsDto[])
        })
      );
  }

  private updateOverviewForMetric(metric: MetricsPlotsDto): void {
    if (metric.boxplot_type === 'dataset_combination') {
      // for status: show overview of first combination by default
      const firstDict = metric.boxplot_dicts[0];
      this.currentOverviewFiles.set(
        firstDict?.overview_files?.length > 0
          ? this.getPlots(firstDict.overview_files)
          : of([])
      );
    } else {
      // for classification metrics: show all overview files
      this.currentOverviewFiles.set(this.getPlots(metric.overview_files));
    }
  }

  onMetricChange(option): void {
    this.selectedMetric.set(option.value);
    // resetting boxplot index
    this.boxplotIndx = 0;
    this.updateOverviewForMetric(option.value);
  }

  private checkZarrFile(): void {
    const validationId = this.validation?.id;
    if (!validationId) {
      this.showInteractiveMap = false;
      return;
    }

    this.http
      .get<{ exists: boolean }>(`/api/${validationId}/check-zarr/`)
      .subscribe({
        next: (response) => {
          this.showInteractiveMap = response.exists;
        },
        error: () => {
          this.showInteractiveMap = false;
        }
      });
  }

  onBoxPlotChange(event): void {
    this.boxplotIndx = event.value.ind;
    const metric = this.selectedMetric();
    if (metric.boxplot_type === 'dataset_combination') {
      // for status: update overview when combination changes
      const selectedDict = metric.boxplot_dicts[event.value.ind];
      this.currentOverviewFiles.set(
        selectedDict?.overview_files?.length > 0
          ? this.getPlots(selectedDict.overview_files)
          : of([])
      );
    }
  }

  isDatasetCombination(metric: MetricsPlotsDto): boolean {
    return metric?.boxplot_type === 'dataset_combination';
  }

  showGallery(index: number = 0, plotType: string): void {
    if (plotType === 'overview') {
      this.activeOverviewIndex = index;
      this.displayOverviewGallery = true;
    } else if (plotType === 'boxplot') {
      this.activeBoxplotIndex = index;
      this.displayBoxplotGallery = true;
    } else {
      this.displayAdditionalPlotGallery = true;
      this.activeAdditionalPlotIndex = index;
    }
  }

  downloadResultFile(validationId: string, fileType: string, fileName: string): void {
    this.validationService.downloadResultFile(validationId, fileType, fileName);
  }

  getPlots(files: any): Observable<PlotDto[]> {
    let params = new HttpParams();
    // handling an empty list added
    if (files.length === 0 || files[0].length === 0) {
      return of([]);
    }

    files.forEach(file => {
      params = params.append('file', file);
    });

    return this.plotService.getPlots(params).pipe(
      catchError((error: CustomHttpError) => {
        this.fileError.set(true);
        this.toastService.showErrorWithHeader(error.errorMessage.header, error.errorMessage.message)
        return EMPTY;
      })
    );
  }

  sanitizePlotUrl(plotBase64: string): SafeUrl {
    return this.plotService.sanitizePlotUrl(plotBase64);
  }

}
